import { Capacitor } from '@capacitor/core';
import {
  APPLE_PRODUCT_IDS_BY_PLAN_KEY,
  type StripeSubscriptionPlanKey,
} from '@nfp-tracker/utils';

export type AppStoreSubscriptionProduct = {
  productId: string;
  displayName: string;
  description: string;
  displayPrice: string;
  price: number | null;
  currencyCode: string | null;
  subscriptionPeriodUnit: 'day' | 'week' | 'month' | 'year' | null;
  subscriptionPeriodValue: number | null;
  introductoryOfferPaymentMode: string | null;
  introductoryOfferPeriodUnit: 'day' | 'week' | 'month' | 'year' | null;
  introductoryOfferPeriodValue: number | null;
};

export type AppStoreTransaction = {
  transactionId: string;
  originalTransactionId: string;
  productId: string;
  purchaseDate: string;
  expirationDate: string | null;
  revocationDate: string | null;
  isUpgraded: boolean;
  appAccountToken: string | null;
  environment: string | null;
  signedTransactionInfo: string | null;
};

type GetProductsResult = {
  products: AppStoreSubscriptionProduct[];
};

type GetEntitlementsResult = {
  entitlements: AppStoreTransaction[];
};

type PurchaseResult = {
  status: 'success' | 'pending' | 'cancelled';
  transaction: AppStoreTransaction | null;
  entitlements: AppStoreTransaction[];
};

type AppStorePurchasesPlugin = {
  getProducts(options: { productIds: string[] }): Promise<GetProductsResult>;
  getEntitlements(): Promise<GetEntitlementsResult>;
  purchase(options: {
    productId: string;
    appAccountToken?: string;
  }): Promise<PurchaseResult>;
  restorePurchases(): Promise<GetEntitlementsResult>;
  presentManageSubscriptions(): Promise<void>;
};

const fallbackPlugin: AppStorePurchasesPlugin = {
  getProducts: async () => ({ products: [] }),
  getEntitlements: async () => ({ entitlements: [] }),
  purchase: async () => {
    throw new Error('App Store purchases are only available on native iOS.');
  },
  restorePurchases: async () => ({ entitlements: [] }),
  presentManageSubscriptions: async () => {
    throw new Error('App Store purchases are only available on native iOS.');
  },
};

const AppStorePurchases =
  typeof Capacitor.registerPlugin === 'function'
    ? Capacitor.registerPlugin<AppStorePurchasesPlugin>('AppStorePurchases')
    : fallbackPlugin;

export const isNativeIosPurchasesAvailable = (): boolean =>
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

export const getIosSubscriptionProducts = async (): Promise<AppStoreSubscriptionProduct[]> => {
  if (!isNativeIosPurchasesAvailable()) {
    return [];
  }

  const { products } = await AppStorePurchases.getProducts({
    productIds: Object.values(APPLE_PRODUCT_IDS_BY_PLAN_KEY),
  });

  return products;
};

export const getIosCurrentEntitlements = async (): Promise<AppStoreTransaction[]> => {
  if (!isNativeIosPurchasesAvailable()) {
    return [];
  }

  const { entitlements } = await AppStorePurchases.getEntitlements();
  return entitlements;
};

export const purchaseIosSubscription = async (
  planKey: StripeSubscriptionPlanKey,
  ownerUserId?: string
): Promise<PurchaseResult> => {
  const productId = APPLE_PRODUCT_IDS_BY_PLAN_KEY[planKey];
  return AppStorePurchases.purchase({
    productId,
    ...(ownerUserId ? { appAccountToken: ownerUserId } : {}),
  });
};

export const restoreIosSubscriptions = async (): Promise<AppStoreTransaction[]> => {
  const { entitlements } = await AppStorePurchases.restorePurchases();
  return entitlements;
};

export const presentIosManageSubscriptions = async (): Promise<void> => {
  await AppStorePurchases.presentManageSubscriptions();
};
